<template lang="pug">
	.sonic-scribe--bottom-panel(:style="commonStyleVars")
		.player-holder
			.player.flex-col.center(ref="player", @click="onPlayerClick")
				.actions-panel
					.flex-row.full-width.justify.align-center.space-large.full-height
						.left.flex-row.align-center.space-small
						.right.flex-row.align-center.space-small
							AudioTrackerButton.small(icon="fas fa-arrow-pointer", :title="$tCaption('Select')", :class="{ active: tool === 'cursor' }", @click="activateTool('cursor')")
							AudioTrackerButton.small(icon="fas fa-scissors", :title="$tCaption('Move edit points to adjust the segment boundaries.')", :class="{ active: tool === 'blade' }", @click="activateTool('blade')")
							AudioTrackerButton.small(icon="fas fa-vector-square", :title="$tCaption('Select a range for editing or playback.')", :class="{ active: tool === 'range' }", @click="activateTool('range')")
							AudioTrackerButton.small(icon="fas fa-arrows-left-right", :title="$tCaption('Move the selected section left or right while keeping other sections in place.')", :class="{ active: tool === 'slip' }", @click="activateTool('slip')")
							AudioTrackerButton.small(icon="far fa-hand", :title="$tCaption('Move the entire bottom timeline for navigation.')", :class="{ active: tool === 'hand' }", @click="activateTool('hand')")
				.canvas(:class="{ 'changing': waveFormChanging }")
					canvas(ref="canvas", width="100", height="120")
					.cloader
				.ruler-line
					.item(v-for="(item, idx) in ruleItems", :style="{ '--pos': `${getRulerPos(idx)}px`}") {{ getRulerTime(idx) }}
				BottomPanelWordsGroup(v-for="group in groups", :key="group.id", :group="group", :info="info")
				.time-line(ref="timeline")
					.indicator
				SelectionTool(v-if="buffer", :info="info", @cut="onCut", @build="$emit('change')")
				.h-scroll
					HorizontalScroll(:window="window", :total="vTotalLength", @scroll="onHScroll", :position="vPosition")
				.height-resizer(@mousedown="onHeightStartResize")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import HorizontalScroll from 'app-core/HorizontalScroll.vue';
	import AudioTrackerButton from 'view/audioTracker/AudioTrackerButton.vue';
	import SelectionTool from './SelectionTool.vue';
	import BottomPanelWordsGroup from './BottomPanelWordsGroup.vue';

	export default {
		translationCategory: 'sonicScribe.BottomPanel',
		name: 'sonic-scribe--bottom-panel',
		components: { HorizontalScroll, AudioTrackerButton, SelectionTool, BottomPanelWordsGroup },
		props: {
			isPlaying: VueTypes.bool.def(false),
			duration: VueTypes.number.def(null),
			height: VueTypes.number.def(100),
			words: VueTypes.array.def(null),
			buffer: VueTypes.any.def(null),
			getSpeakerColor: VueTypes.any.def(null),
			groups: VueTypes.array.def([]),
			sonicScribeSplitGroup: VueTypes.any.def(null)
		},
		data() {
			return {
				window: 100,
				position: 0,
				channelData: null,
				waveFormChanging: false,
				isResizingHeight: false,
				heights: {
					actions: 30,
					ruler: 14,
					words: 30
				},
				tool: 'cursor'
			};
		},
		computed: {
			pixelPerSecond() {
				return 300;
			},
			vTotalLength() {
				return this.duration * this.pixelPerSecond;
			},
			vPosition() {
				return this.position / this.duration * 100;
			},
			audioWindow() {
				return this.window / this.pixelPerSecond;
			},
			ruleItems() {
				return Math.ceil(this.audioWindow);
			},
			commonStyleVars() {
				return {
					'--height': `${this.height}px`, 
					'--width': `${this.window}px`, 
					'--hActions': `${this.heights.actions}px`,
					'--hRuler': `${this.heights.ruler}px`,
					'--hWords': `${this.heights.words}px`,
					'--canvasTop': `${Object.keys(this.heights).reduce((r, v) => r + this.heights[v], 0)}px`,
				}
			},
			info() {
				return {
					position: this.position,
					audioWindow: this.audioWindow,
					pixelPerSecond: this.pixelPerSecond,
					heights: this.heights,
					window: this.window,
					groups: this.groups,
					words: this.words,
					selectionMode: this.tool,
					getDisplayPosition: this.getDisplayPosition,
					buffer: this.buffer
				}
			}
		},
		watch: {
			buffer() {
				this.prepareChannelData(true);
				this.$drawImage('buffer');
				this.waveFormChanging = true;
			},
			position() {
				this.$drawImage('position');
				this.waveFormChanging = true;
			},
			height() {
				this.$drawImage('height');
				this.waveFormChanging = true;
			}
		},
		mounted() {
			this.$setWidthDebounced = _.debounce(() => this.setWidth(), 100);
			window.addEventListener('resize', this.$setWidthDebounced);
			document.body.addEventListener('mouseup', this.onBodyMouseUp);
			document.body.addEventListener('mousemove', this.onBodyMouseMove);

			if (__DEBUG__)
				window.bottomPanel = this;

			this.$eventsBus.$on('sonic-scribe-timeline', this.onSonicScribeTimeline);

			this.$drawImage = _.debounce(this.drawImage, 50, {
				leading: false,
				trailing: true
			});

			this.setWidth();
		},
		destroyed() {
			delete this.$setWidthDebounced;

			window.removeEventListener('resize', this.$setWidthDebounced);
			document.body.removeEventListener('mouseup', this.onBodyMouseUp);
			document.body.removeEventListener('mousemove', this.onBodyMouseMove);
			this.$eventsBus.$off('sonic-scribe-timeline', this.onSonicScribeTimeline);
		},
		methods: {
			onCut(time) {
				if (__DEBUG__)
					console.debug(`OnCut ${time}`);

				if (time) {
					this.sonicScribeSplitGroup(time);
				}
			},
			activateTool(toolId) {
				this.tool = toolId;
			},
			onPlayerClick(e) {
				if (__DEBUG__)
					console.debug('onPlayerClick', { e });

				let check = (node) => node?.classList?.contains('player');
				if (check(e.srcElement) || check(e.srcElement?.parentElement)) {
					this.$eventsBus.$emit('sonic-scribe-timeline', this.position + e.offsetX / this.pixelPerSecond, true);
				}
			},
			prepareChannelData(force) {
				if ((!this.channelData || force) && this.buffer) {
					this.channelData = this.buffer.getChannelData(0);
				}
				return this.channelData;
			},
			drawImage(log) {
				if (__DEBUG__)
					console.debug(`DrawImage ${log}`);

				let buffer = this.buffer;
				if (!buffer?.length) return;

				let width = this.window;
				const leftChannel = this.prepareChannelData();
				const realCanvasWidth = buffer.duration * this.pixelPerSecond;
				const drawLines = parseInt(realCanvasWidth);
				const totallength = leftChannel.length;
				const detalization = 10;
				const lineGap = (realCanvasWidth / drawLines) / detalization;
				const eachBlock = Math.floor(totallength / drawLines) / detalization;

				const canvasScale = 2;
				const imageHeight = this.height - Object.keys(this.heights).reduce((r, key) => r + this.heights[key], 0);
				let canvasObj = this.$refs.canvas;
				let context = canvasObj.getContext('2d');
				canvasObj.width = width * canvasScale;
				canvasObj.height = imageHeight * canvasScale;

				context.scale(canvasScale, canvasScale);

				let paintStart = this.position * this.pixelPerSecond;
				let paintEnd = (this.position + this.audioWindow) * this.pixelPerSecond;

				context.save();
				context.fillStyle = 'transparent';
				context.fillRect(0, 0, (paintEnd - paintStart), imageHeight);
				context.strokeStyle = '#333';
				context.translate(0, imageHeight / 2);
				context.lineWidth = 0.25;

				let coords = [];

				const getValue = (ch) => {
					const block = Math.max(Math.min(200, eachBlock), 20);
					const blocks = [];
					for(let i = 0; i < ch.length; i += block) {
						blocks.push(_.sum(ch.slice(0, i - 1)) / block);
					}
					return _.sum(blocks) / blocks.length;
				}

				let currentSpeaker = -1;
				let colors = [];
				for(var k = paintStart; k <= paintEnd; k += lineGap) {
					let start = Math.floor(parseFloat(k / realCanvasWidth) * parseFloat(totallength));
					let end = start + eachBlock;
					if (start > totallength) break;
					const value = getValue(leftChannel.slice(start, end));
					var x = Math.min(paintEnd, k) - paintStart;
					var y1 = value * (imageHeight / 1) * 1.5 || 0.1;

					coords.push([x,y1]);

					let time = start / buffer.sampleRate;
					let word = this.words.find(f => (f.actualStart + f.Duration) > time);

					if (word && currentSpeaker !== word.Speaker) {
						currentSpeaker = word.Speaker;
						colors.push({
							x,
							color: this.getSpeakerColor(currentSpeaker)
						});
					}
				}

				var grad = context.createLinearGradient(0, 25, 0, -25);
				grad.addColorStop(0, '#fd2f2f');
				grad.addColorStop(0.5, '#ff4910');
				grad.addColorStop(1, '#ffa300');

				const maxY = _.max(coords.map(f => Math.abs(f[1])));

				let heightCoef = 0.75;

				coords.map((c, i) => {
					const last = coords[i-1];
					if (i > 0) {
						let color = colors.filter(f => f.x <= c[0]).slice(-1)[0]?.color;
						context.lineWidth = 0.25;
						context.strokeStyle = color || grad;
						context.beginPath();
						context.moveTo(last[0], last[1] / maxY * 0.5 * imageHeight * heightCoef);
						context.lineTo(c[0], c[1] / maxY * 0.5 * imageHeight * heightCoef);
						context.stroke();
					}
				});

				setTimeout(() => {
					this.waveFormChanging = false;
				}, 100);
			},
			getRulerTime(idx) {
				return this.$Data.secondsToDisplayValue(idx + Math.ceil(this.position));
			},
			getRulerPos(idx) {
				let delta = Math.ceil(this.position) - this.position;
				return (idx + delta) * this.pixelPerSecond;
			},
			calcTimeline(v) {
				v = v || this.$playTime || 0;
				if (this.$refs?.timeline?.style) {
					this.$refs.timeline.style.setProperty('--pos', `${(v - this.position) * this.pixelPerSecond}px`);
				}
			},
			onSonicScribeTimeline(v, opts) {
				this.$playTime = v;

				if (v > (this.position + this.audioWindow)) {
					this.position = v - this.audioWindow / 2;
				} else if (v < this.position) {
					this.position = v;
				}

				this.calcTimeline();
			},
			onHeightStartResize() {
				this.isResizingHeight = true;
				this.movingDelta = 0;
			},
			onBodyMouseUp() {
				this.isResizingHeight = false;
			},
			onBodyMouseMove(e) {
				if (this.isResizingHeight) {
					this.$emit('height-change', this.height - e.movementY);
				}
			},
			getDisplayPosition(v) {
				return (v - this.position) * this.pixelPerSecond;
			},
			onHScroll(v) {
				let newPos = Math.max(0, (this.duration - this.audioWindow) * v);
				if (newPos !== this.position) {
					this.position = newPos;
				}
				this.calcTimeline();
			},
			setPosition(v) {
				this.$refs.audioSrc.currentTime = v;
				this.$refs.waveForm.waveform();
			},
			setWidth() {
				if (__DEBUG__)
					console.info('Resize player');

				this.window = this.$el?.offsetWidth + 40;
				this.$drawImage();
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.sonic-scribe--bottom-panel {
		> .player-holder {
			position: relative;
			height: var(--height);
			width: 100%;

			> .player {
				position: absolute;
				transition: 0.5s;
				align-items: center;
				height: calc(100% + 6px);
				user-select: none;
				bottom: -6px;
				left: 0px;
				left: -20px;
				right: -20px;
				overflow: hidden;
			}

			.h-scroll {
				position: absolute;
				bottom: 0px;
				width: 100%;
			}
		}

		.actions-panel {
			top: 0px;
			position: absolute;
			height: var(--hActions);
			width: 100%;
			background: #efefef;
			padding: 4px;
			border-top: 1px solid #e7e7e7;
			pointer-events: none;
			
			.audio-tracker-button {
				pointer-events: all;
			}
		}

		.ruler-line {
			position: absolute;
			top: var(--hActions);
			width: 100%;
			left: 0px;
			height: var(--hRuler);
			background: #fff;
			overflow: hidden;
			border-top: 1px solid #e9e9e9;
			font-size: 10px;
			color: #aaa;
			background: #efefef;
			pointer-events: none;

			> .item {
				position: absolute;
				left: var(--pos);
				height: 100%;
				border-left: 1px solid #ddd;
				padding-left: 3px;
			}
		}

		.time-line {
			position: absolute;
			width: 100%;
			height: 100%;
			overflow: hidden;
			pointer-events: none;

			> .indicator {
				position: absolute;
				left: var(--pos);
				top: var(--hActions);
				height: 100%;
				width: 0.5px;
				background: #ff4646;
			}
		}

		.canvas {
			position: absolute;
			left: 0px;
			top: calc(var(--hActions) + var(--hRuler) +  var(--hWords));
			bottom: 0px;
			width: 100%;
			overflow: hidden;
			pointer-events: none;
			background: #e9e9e9;

			> canvas {
				width: var(--width);
				transition: 0.1s;
				transform: scaleY(1);
			}

			.cloader {
				position: absolute;
				width: 100%;
				height: 1px;
				top: 50%;
				transform: translateY(-50%) scaleY(0);
				background: $base-accent;
				transition: 0.1s;
			}

			&.changing {
				> canvas {
					transform: scaleY(0);
				}

				.cloader {
					transform: translateY(-50%) scaleY(0.25);
				}
			}
		}

		.busy {
			opacity: 0.3;
		}

		.height-resizer {
			position: absolute;
			top: calc(-5px);
			height: 10px;
			width: 100%;
			cursor: s-resize;
			transition: 0.2s;

			&:before {
				position: absolute;
				content: '';
				top: 5px;
				width: 0%;
				height: 2px;
				background: transparent;
				transition: 0.15s;
				left: 50%;
			}

			&:hover {
				&:before {
					width: 100%;
					left: 0%;
					background: $base-accent;
				}
			}
		}
	}
</style>
